import _ from 'lodash'

import { CustomDevice, Device } from '@/core/models/custom-device.model'

import { MEDICAL_CAM, STETO, VISIO_CONF, VISIO_CONF_MIC } from '@/constants/device-types'
import { defaultDevices } from '@/constants/devices'

/**
 * Normalizes a device label for comparison by lowercasing and
 * collapsing punctuation and extra whitespace into single spaces.
 */
function normalizeDeviceLabel(s: string): string {
  return s.toLowerCase().replace(/[-_().!,]/g, ' ').replace(/\s+/g, ' ').trim()
}

/**
 * Returns true when the OS-reported device label is a plausible match for a
 * known device name. Handles patterns like "Microphone (PCP-USB) (0d8c:0134)"
 * matching "PCP-USB".
 */
function deviceLabelMatches(osLabel: string, searchName: string): boolean {
  const label = normalizeDeviceLabel(osLabel)
  const search = normalizeDeviceLabel(searchName)

  if (label.includes(search)) return true
  if (search.includes(label)) return true

  const tokens = search.split(' ').filter((t) => t.length > 1)
  return tokens.length > 0 && tokens.every((token) => label.includes(token))
}

function getDefaultCustomDevices(mediaDevices: MediaDeviceInfo[]): Array<CustomDevice & { available?: boolean }> {
  const defaultCustomDevices: Array<CustomDevice & { available?: boolean }> = []

  defaultDevices.forEach((value) => {
    const mediaDevice = mediaDevices.find((md) => deviceLabelMatches(md.label, value.name))

    if (!!mediaDevice) {
      defaultCustomDevices.push({
        ...value,
        _id: mediaDevice.deviceId,
        device: mapMediaDeviceToDevice(mediaDevice),
        owner: '',
      })
    }
  })

  return defaultCustomDevices
}

function getCustomDevicesBy(customDeviceType: string, deviceKind: MediaDeviceKind) {
  return (customDevices: Array<CustomDevice & { available?: boolean }>, mediaDevices: MediaDeviceInfo[]) => {
    return _.filter(
      _.uniqBy(
        _.concat(
          _.map(customDevices, (customDevice) => {
            const mediaDevice = _.find(mediaDevices, (md) =>
              md.label === customDevice.device.name || deviceLabelMatches(md.label, customDevice.device.name)
            )
            return _.merge({}, customDevice, { available: !!mediaDevice, device: mediaDevice ? mapMediaDeviceToDevice(mediaDevice) : undefined })
          }),
          getDefaultCustomDevices(mediaDevices)
        ),
        'device.deviceId'
      ),
      (customDevice) => !!customDevice.available && customDevice.device.type === deviceKind && customDevice.type === customDeviceType
    )
  }
}

export async function getUserMedia(constraints: MediaStreamConstraints, cb?: (error: unknown, stream?: MediaStream) => void) {
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints)
    cb?.(null, stream)
  } catch (error) {
    console.log(error)
    cb?.(error)
  }
}

function mapMediaDeviceToDevice(mediaDevice: MediaDeviceInfo): Device {
  return {
    deviceId: mediaDevice.deviceId,
    name: mediaDevice.label,
    type: mediaDevice.kind,
  }
}

export function rejectDefaultDevices<D extends { label: string }>(devices: D[]) {
  return _.reject(devices, (device) => defaultDevices.some((defaultDevice) => device.label.toLowerCase().includes(defaultDevice.name.toLowerCase())))
}

export const getMedicalCameras = getCustomDevicesBy(MEDICAL_CAM, 'videoinput')

export const getStethoscopes = getCustomDevicesBy(STETO, 'audioinput')

export const getVisioConferenceCameras = getCustomDevicesBy(VISIO_CONF, 'videoinput')

export const getVisioConferenceMicros = getCustomDevicesBy(VISIO_CONF_MIC, 'audioinput')
